/**
 * Kleine IndexedDB-Ablage für die Offline-Bearbeitung (0.44): zwischengespeicherte Daten und
 * ausstehende Änderungen, immer je Konto getrennt. Ohne IndexedDB (privates Fenster, Test) bleibt
 * alles im Speicher – die App funktioniert dann wie bisher, nur ohne Offline-Stand nach dem Neuladen.
 */
const DB = 'kaba-offline';
const VERSION = 1;
export type StoreName = 'cache' | 'outbox';

let opening: Promise<IDBDatabase | null> | null = null;
function open(): Promise<IDBDatabase | null> {
  if (opening) return opening;
  opening = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache');
        if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox');
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

/** Ersatz ohne IndexedDB */
const memory: Record<StoreName, Map<string, unknown>> = { cache: new Map(), outbox: new Map() };

function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) return resolve(undefined);
        try {
          const t = db.transaction(store, mode);
          const req = fn(t.objectStore(store));
          t.oncomplete = () => resolve(req ? req.result : undefined);
          t.onerror = () => resolve(undefined);
          t.onabort = () => resolve(undefined);
        } catch (e) {
          console.warn('Offline-Ablage', store, e);
          resolve(undefined);
        }
      }),
  );
}

export async function idbPut(store: StoreName, key: string, value: unknown): Promise<void> {
  memory[store].set(key, value);
  await tx(store, 'readwrite', (s) => s.put(value, key));
}

export async function idbDelete(store: StoreName, key: string): Promise<void> {
  memory[store].delete(key);
  await tx(store, 'readwrite', (s) => s.delete(key));
}

/** Alle Einträge mit Schlüsselpräfix (z. B. `<konto>|`) */
export async function idbEntries<T>(store: StoreName, prefix: string): Promise<[string, T][]> {
  const db = await open();
  if (!db) return [...memory[store].entries()].filter(([k]) => k.startsWith(prefix)) as [string, T][];
  const range = IDBKeyRange.bound(prefix, `${prefix}￿`);
  const [keys, values] = await Promise.all([tx<IDBValidKey[]>(store, 'readonly', (s) => s.getAllKeys(range)), tx<T[]>(store, 'readonly', (s) => s.getAll(range))]);
  return (keys ?? []).map((k, i) => [String(k), values![i]!]);
}

/** Alles eines Kontos löschen (Abmelden, Offline ausgeschaltet, Sitzung abgelaufen) */
export async function idbClearPrefix(store: StoreName, prefix: string): Promise<void> {
  for (const k of [...memory[store].keys()]) if (k.startsWith(prefix)) memory[store].delete(k);
  const db = await open();
  if (!db) return;
  await tx(store, 'readwrite', (s) => s.delete(IDBKeyRange.bound(prefix, `${prefix}￿`)));
}
